<script setup>

import {computed, inject, reactive, ref} from "vue";
import ConfirmModal from "@/components/ui/ConfirmModal.vue";
import {getNoun, getNumberWithNoun} from "@/helpers/string";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import needsService from "@/services/needs.service";

const need = inject('need');
const solutions = inject('solutions');
const getStatus = inject('getStatus');

const showConfirmModal = ref(false);
const canForceEscalate = ref(false);
const showModal = ref(false);
const showSuccessModal = ref(false);
const loading = ref(false);

const form = reactive({
    comment: ''
});

const solutionsCount = computed(() => solutions.value.length);
const reviewedSolutionsCount = computed(() => solutions.value.filter(startup => startup.rating > 0).length);
const unreviewedSolutionsCount = computed(() => solutions.value.filter(startup => !startup.rating).length);

function requestEscalate() {
    if (solutionsCount.value === reviewedSolutionsCount.value || canForceEscalate.value) {
        showModal.value = true;
    } else {
        showConfirmModal.value = true;
    }
}

function closeConfirmed(option) {
    showConfirmModal.value = false;

    if (option === 'escalate') {
        canForceEscalate.value = true;
        requestEscalate();
    }
}

async function escalate() {
    loading.value = true;
    const {data} = await needsService.escalate(need.value.id, form);

    if (data) {
        showModal.value = false;
        showSuccessModal.value = true;

        getStatus();
    }

    loading.value = false;
}
</script>

<template>
    <div>
        <a @click="requestEscalate" class="btn-xl btn-xl-export mb-3">Escalate Need</a>

        <p class="mb-0"><b>Escalate</b> to send this need and your assessed solutions to your innovation team for further review</p>

        <ConfirmModal
            :show.sync="showConfirmModal"
            title="Unreviewed Solutions"
            :body="`There ${getNoun('is', unreviewedSolutionsCount, 'are')} ${getNumberWithNoun('solution', unreviewedSolutionsCount)} that you have not reviewed. Are you sure you want to escalate this need`"
            :options="[
                {label: 'Cancel', value: 'cancel', variant: 'danger'},
                {label: 'Escalate', value: 'escalate'}
            ]"
            @clicked="closeConfirmed"
        />

        <b-modal v-model="showModal" title="Escalate Need" modal-class="modal-w-lg">
            <p>
                Escalating this need will send this project along with your solutions and reviews to your innovation team. You will no longer be able to make any changes to it.
            </p>

            <div>
                <p>{{need.name}}</p>

                <p>{{need.description}}</p>
            </div>

            Solutions: {{getNumberWithNoun('startup', solutionsCount) }}
            Recommended: {{getNumberWithNoun('startup', solutionsCount) }}
            Reviewed: {{getNumberWithNoun('startup', reviewedSolutionsCount) }}

            <form class="form">
                <div class="form__item">
                    <b-form-textarea
                        v-model="form.comment"
                        class="form-control--mid mb-3"
                        placeholder="Add a note (optional)"
                    />
                </div>
            </form>

            <template #modal-footer>
                <PrimaryButton
                    caption="Escalate Need"
                    :loading="loading"
                    @click="escalate"
                />
            </template>
        </b-modal>

        <b-modal v-model="showSuccessModal" title="Need Escalated Successfully">
            <p>Your need, solutions and ratings were sent to your innovation team.</p>

            <template #modal-footer>
                <PrimaryButton
                    caption="Close"
                    @click="showSuccessModal = false"
                />
            </template>
        </b-modal>
    </div>
</template>

<style scoped>

</style>
